import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { PriorityPill, StatusPill } from "@/components/Pills";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Settings2, CopyX } from "lucide-react";

export default function CustomerDatabase() {
  const { meta, user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [rms, setRms] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [rm, setRm] = useState("All");
  const [melaInt, setMelaInt] = useState("All");
  const [edit, setEdit] = useState(null);

  const load = useCallback(() => {
    api.get("/customers", { params: { search, lead_status: status, rm, mela_interest: melaInt } }).then((r) => setRows(r.data));
  }, [search, status, rm, melaInt]);

  useEffect(() => { api.get("/rms").then((r) => setRms(r.data)); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const doReassign = async (cid, rm_username) => {
    await api.patch(`/customers/${cid}/reassign`, { rm_username });
    toast.success("Reassigned"); setEdit(null); load();
  };
  const doStatus = async (cid, lead_status) => {
    await api.patch(`/customers/${cid}/status`, { lead_status });
    toast.success("Status updated"); setEdit(null); load();
  };

  return (
    <div>
      <PageHeader title="Customer Database" subtitle={`${rows.length} records${user?.role === "branch_manager" ? " in your branch" : " across all RMs"}`}
        actions={user?.role === "admin" ? <Button variant="outline" onClick={() => navigate("/app/duplicates")} data-testid="find-duplicates-btn"><CopyX size={16} className="mr-2" />Find Duplicates</Button> : null} />
      <Card className="p-4 mb-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="db-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="pl-9" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-52" data-testid="db-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="All">All Statuses</SelectItem>{meta.lead_stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={rm} onValueChange={setRm}>
            <SelectTrigger className="w-44" data-testid="db-rm-filter"><SelectValue placeholder="RM" /></SelectTrigger>
            <SelectContent><SelectItem value="All">All RMs</SelectItem>{rms.map((r) => <SelectItem key={r.username} value={r.username}>{r.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={melaInt} onValueChange={setMelaInt}>
            <SelectTrigger className="w-44" data-testid="db-mela-filter"><SelectValue placeholder="Mela Interest" /></SelectTrigger>
            <SelectContent><SelectItem value="All">Mela: All</SelectItem><SelectItem value="Yes">Mela: Yes</SelectItem><SelectItem value="No">Mela: No</SelectItem></SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Business</TableHead><TableHead>Mobile</TableHead><TableHead>RM</TableHead>
              <TableHead>Priority</TableHead><TableHead>Loan</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={c.id} data-testid={`db-row-${c.id}`}>
                <TableCell className="font-semibold text-slate-900 cursor-pointer" onClick={() => navigate(`/app/customers/${c.id}`)}>{c.business_name}</TableCell>
                <TableCell className="text-sm">{c.mobile}</TableCell>
                <TableCell className="text-sm">{c.rm_username}</TableCell>
                <TableCell><PriorityPill value={c.priority} /></TableCell>
                <TableCell className="font-mono text-sm">{inr(c.loan_amount)}</TableCell>
                <TableCell><StatusPill value={c.lead_status} /></TableCell>
                <TableCell>
                  <Dialog open={edit === c.id} onOpenChange={(o) => setEdit(o ? c.id : null)}>
                    <DialogTrigger asChild><Button size="sm" variant="outline" data-testid={`manage-${c.id}`}><Settings2 size={14} /></Button></DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>Manage · {c.business_name}</DialogTitle></DialogHeader>
                      <div className="space-y-4 pt-2">
                        <div>
                          <div className="text-sm font-semibold mb-2">Reassign RM</div>
                          <Select defaultValue={c.rm_username} onValueChange={(v) => doReassign(c.id, v)}>
                            <SelectTrigger data-testid={`reassign-select-${c.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{rms.map((r) => <SelectItem key={r.username} value={r.username}>{r.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <div className="text-sm font-semibold mb-2">Change Status</div>
                          <Select defaultValue={c.lead_status} onValueChange={(v) => doStatus(c.id, v)}>
                            <SelectTrigger data-testid={`status-select-${c.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{meta.lead_stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">No records.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
